import React, { FC, useMemo } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { generateTheme } from '@clds/theme-helper';
import light, { dark } from '@clds/themes';
import { overrideTheme } from '@clds/component-theme';
import { usePhone } from '@clds/style-utils';
import {
  SingleCodeSnippet,
  SingleCodeSnippetMobile,
  SingleCodeSnippetProps,
  StyledEllipsisText,
  VersionedCodeSnippetProps,
  t as codeSnippetTheme,
} from '@cld/code-snippet-next';
import { SdkCodeSnippetsPure } from './SdkCodeSnippetsPure';
import { getThemeConfiguration } from './utils/getThemeConfiguration';
import { SdkCodeSnippetsLayout } from './components/SdkCodeSnippetsLayout';
import { SdkCodeSnippetsMobilePure } from './SdkCodeSnippetsMobilePure';
import { useActiveSnippetChange, useSelectedSnippet, useSnippetPersistState, useSnippetSyncState } from './hooks';
import { OnTabChange, SdkCodeSnippetsPureProps } from './types';
import { OpenInTransformationBuilderButton } from './components/OpenInTransformationBuilderButton/OpenInTransformationBuilderButton';
import { shouldOpenTransformationBuilder } from './components/OpenInTransformationBuilderButton';

export interface SdkCodeSnippetsConnectedProps extends SdkCodeSnippetsPureProps {
  theme?: 'light' | 'dark';
  autoSync?: boolean;
  languageMenu?: 'menu' | 'text' | 'none';
}

export const StyledSdkCodeSnippetsLayout = styled(SdkCodeSnippetsLayout)`
  ${StyledEllipsisText} {
    max-width: 300px;
  }
`;

/**
 * local storage communication
 */
export const SdkCodeSnippetsConnected: FC<React.PropsWithChildren<SdkCodeSnippetsConnectedProps>> = ({
  snippets,
  theme = 'light',
  withBackground = true,
  autoSync = false,
  onSnippetChange,
  onTabChange,
  onCopy,
  parsedUrl,
  languageMenu = 'menu',
  height,
  fontSize,
  lineHeight,
  headerSize,
  openInBuilderButtonConfig,
  ...rest
}) => {
  const sdkCodeSnippetTheme = useMemo(
    () => ({
      ...generateTheme(theme === 'dark' ? dark : light),
      components: overrideTheme(codeSnippetTheme, { code: getThemeConfiguration({ fontSize, lineHeight }) }),
    }),
    [theme, fontSize, lineHeight]
  );

  const isPhone = usePhone();
  const PureComponent = (isPhone ? SdkCodeSnippetsMobilePure : SdkCodeSnippetsPure) as FC<React.PropsWithChildren<VersionedCodeSnippetProps>>;
  const SingleSnippetComponent = (isPhone ? SingleCodeSnippetMobile : SingleCodeSnippet) as unknown as FC<React.PropsWithChildren<SingleCodeSnippetProps>>;
  const identifier = useMemo(uuidv4, []);
  const { language, setLanguage, version, setVersion } = useSnippetPersistState(snippets);
  const activeSnippet = useSelectedSnippet(snippets, language, version);
  const isAutoSync = !!(languageMenu === 'menu' && autoSync);
  const { handleLanguageChange, handleVersionChange } = useSnippetSyncState(isAutoSync, snippets, setLanguage, setVersion, identifier, activeSnippet);
  const onCopySnippet = () => onCopy?.(activeSnippet);
  const onChangeTab = () => onTabChange?.(language);

  useActiveSnippetChange(activeSnippet, onSnippetChange, onChangeTab, language, version);

  return (
    <ThemeProvider theme={sdkCodeSnippetTheme}>
      <StyledSdkCodeSnippetsLayout id={identifier} withBackground={withBackground}>
        {languageMenu !== 'menu' ? (
          <SingleSnippetComponent
            snippet={snippets[0].versions[0].snippet}
            syntax={snippets[0].versions[0].syntax || ''}
            language={snippets[0].language}
            title={languageMenu === 'text' ? snippets[0].language : ''}
            onCopy={onCopySnippet}
            parsedUrl={parsedUrl}
            headerSize={headerSize}
          />
        ) : (
          <PureComponent
            onLanguageChange={handleLanguageChange}
            activeLanguage={language}
            langVersion={version}
            onLangVersionChange={handleVersionChange}
            snippets={snippets}
            onCopy={onCopySnippet}
            parsedUrl={parsedUrl}
            height={height}
            headerSize={headerSize}
            footer={
              openInBuilderButtonConfig?.enabled && shouldOpenTransformationBuilder(parsedUrl) ? (
                <OpenInTransformationBuilderButton host={openInBuilderButtonConfig.host} target={openInBuilderButtonConfig.target} parsedUrl={parsedUrl} />
              ) : undefined
            }
            {...rest}
          />
        )}
      </StyledSdkCodeSnippetsLayout>
    </ThemeProvider>
  );
};
